import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastProvider } from "./components";
import { AchatPage } from "./pages/AchatPage";
import { CataloguePage } from "./pages/CataloguePage";
import { CommandePage } from "./pages/CommandePage";
import { DashboardPage } from "./pages/DashboardPage";
import { HistoriquePage } from "./pages/HistoriquePage";
import { MissionsPage } from "./pages/MissionsPage";
import { AuthLayout } from "./pages/auth/AuthLayout";
import { ConnexionPage } from "./pages/auth/ConnexionPage";
import { InscriptionPage } from "./pages/auth/InscriptionPage";
import { InvitationPage } from "./pages/auth/InvitationPage";
import { MotDePasseOubliePage } from "./pages/auth/MotDePasseOubliePage";
import { ReinitialisationPage } from "./pages/auth/ReinitialisationPage";
import { EsnConsultantsPage } from "./pages/esn/EsnConsultantsPage";
import { EsnEntreprisePage } from "./pages/esn/EsnEntreprisePage";
import { EsnEntreprisesPage } from "./pages/esn/EsnEntreprisesPage";
import { EsnEquipePage } from "./pages/esn/EsnEquipePage";
import { EsnConnexionsPage } from "./pages/esn/EsnConnexionsPage";
import { EsnMarquePage } from "./pages/esn/EsnMarquePage";
import { EsnInscriptionsPage } from "./pages/esn/EsnInscriptionsPage";
import { ConsultantFeuillePage } from "./pages/feuilles/ConsultantFeuillePage";
import { ConsultantFeuillesPage } from "./pages/feuilles/ConsultantFeuillesPage";
import { FeuillePage } from "./pages/feuilles/FeuillePage";
import { FeuillesPage } from "./pages/feuilles/FeuillesPage";
import { EquipePage } from "./pages/parametres/EquipePage";
import { MarquePage } from "./pages/parametres/MarquePage";
import { PaiementPage } from "./pages/parametres/PaiementPage";
import { VitrinePage } from "./pages/vitrine/VitrinePage";
import { COMPANY_ROLES, CONSULTANT_ROLES, ESN_ROLES, SessionProvider } from "./session";
import {
  AccueilRedirect,
  ConsultantLayout,
  EntrepriseLayout,
  EsnEntrepriseLayout,
  EsnLayout,
  FeuilleAiguillage,
  ROUTES,
  RequireAnonymous,
  RequireAuth,
  RequirePermission,
  RequireRole,
} from "./shell";

/**
 * Câblage de l'application.
 *
 * La pile de messages enveloppe tout ; la session vit sous le routeur parce
 * qu'elle navigue à la déconnexion ; les gardes décident, déclarativement, qui
 * voit quoi. Quatre espaces : public, entreprise, consultant, ESN.
 *
 * La vitrine, à la racine, est visible connecté comme anonyme : son en-tête
 * s'adapte à la session, et c'est utile en démonstration.
 */
export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <SessionProvider>
          <Routes>
            <Route path={ROUTES.accueil} element={<VitrinePage />} />

            {/* Public : la connexion, l'oubli et l'inscription sont réservés
                aux anonymes ; l'invitation et la réinitialisation, non — un
                invité peut être connecté sous un autre compte, et
                l'acceptation ouvre la sienne. */}
            <Route element={<RequireAnonymous />}>
              <Route element={<AuthLayout />}>
                <Route path={ROUTES.connexion} element={<ConnexionPage />} />
                <Route path={ROUTES.motDePasseOublie} element={<MotDePasseOubliePage />} />
              </Route>
              <Route element={<AuthLayout wide />}>
                <Route path={ROUTES.inscription} element={<InscriptionPage />} />
              </Route>
            </Route>
            <Route element={<AuthLayout />}>
              <Route path={ROUTES.reinitialisation} element={<ReinitialisationPage />} />
              <Route path={ROUTES.invitation} element={<InvitationPage />} />
            </Route>

            <Route element={<RequireAuth />}>
              {/* La cible des courriels de feuille de temps, commune aux
                  rôles : l'aiguillage renvoie chacun dans son espace, et rend
                  la coquille entreprise pour un compte d'entreprise. */}
              <Route path={ROUTES.feuille} element={<FeuilleAiguillage />}>
                <Route index element={<FeuillePage espace="entreprise" />} />
              </Route>

              {/* Espace consultant : ses missions et ses feuilles, rien du
                  portefeuille. */}
              <Route element={<RequireRole roles={CONSULTANT_ROLES} otherwise="home" />}>
                <Route element={<ConsultantLayout />}>
                  <Route path={ROUTES.consultant} element={<ConsultantFeuillesPage />} />
                  <Route path={ROUTES.consultantFeuille} element={<ConsultantFeuillePage />} />
                </Route>
              </Route>

              {/* Espace entreprise : les rôles d'entreprise seuls ; un compte
                  ESN qui arrive ici est renvoyé chez lui. */}
              <Route element={<RequireRole roles={COMPANY_ROLES} otherwise="home" />}>
                <Route element={<EntrepriseLayout />}>
                  <Route path={ROUTES.dashboard} element={<DashboardPage />} />
                  <Route path={ROUTES.achat} element={<AchatPage />} />
                  <Route path={ROUTES.catalogue} element={<CataloguePage />} />
                  <Route path={ROUTES.commande} element={<CommandePage />} />
                  <Route path={ROUTES.missions} element={<MissionsPage />} />
                  <Route path={ROUTES.historique} element={<HistoriquePage />} />
                  <Route element={<RequirePermission permission="can_review_timesheets" />}>
                    <Route path={ROUTES.feuilles} element={<FeuillesPage espace="entreprise" />} />
                  </Route>
                  <Route element={<RequireRole roles={["admin"]} />}>
                    <Route path={ROUTES.parametresEquipe} element={<EquipePage />} />
                    <Route path={ROUTES.parametresMarque} element={<MarquePage />} />
                    <Route path={ROUTES.parametresPaiement} element={<PaiementPage />} />
                  </Route>
                </Route>
              </Route>

              {/* Espace ESN. */}
              <Route element={<RequireRole roles={ESN_ROLES} otherwise="home" />}>
                <Route element={<EsnLayout />}>
                  <Route path={ROUTES.esnEntreprises} element={<EsnEntreprisesPage />} />
                  <Route path={ROUTES.esnInscriptions} element={<EsnInscriptionsPage />} />
                  <Route path={ROUTES.esnEntreprise} element={<EsnEntrepriseLayout />}>
                    <Route index element={<EsnEntreprisePage />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="commande" element={<CommandePage />} />
                    <Route path="missions" element={<MissionsPage />} />
                    <Route path="historique" element={<HistoriquePage />} />
                    <Route path="achat" element={<AchatPage />} />
                  </Route>
                  <Route path={ROUTES.esnCatalogue} element={<CataloguePage />} />
                  <Route path={ROUTES.esnFeuilles} element={<FeuillesPage espace="esn" />} />
                  <Route path={ROUTES.esnFeuille} element={<FeuillePage espace="esn" />} />
                  <Route path={ROUTES.esnConsultants} element={<EsnConsultantsPage />} />
                  <Route element={<RequireRole roles={["esn_admin"]} />}>
                    <Route path={ROUTES.esnEquipe} element={<EsnEquipePage />} />
                    <Route path={ROUTES.esnMarque} element={<EsnMarquePage />} />
                    <Route path={ROUTES.esnConnexions} element={<EsnConnexionsPage />} />
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* Un chemin inconnu ramène chez soi, ou à la vitrine. */}
            <Route path="*" element={<AccueilRedirect />} />
          </Routes>
        </SessionProvider>
      </BrowserRouter>
    </ToastProvider>
  );
}
